import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Info, XCircle } from 'lucide-react';
import { api } from '../lib/api';
import { cn, formatDate, formatHours, formatMoney } from '../lib/utils';
import { ICON, Notice, Skeleton } from '../components/ui';
import type { CompareResult } from '../types';

/**
 * Dag-voor-dag vergelijking van je eigen diensten met de urencontrole en loonstrook van Trigion.
 * Wordt getoond in het periodepaneel op "Loon controleren" (/afwijkingen); /vergelijking verwijst daarheen.
 */

const signed = (n: number, fmt: (n: number) => string) => `${n > 0 ? '+' : ''}${fmt(n)}`;

export function PeriodeVergelijking({ year, period }: { year: number; period: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['compare', year, period],
    queryFn: () => api.compare.get(year, period),
  });

  if (error) return <Notice tone="err">De vergelijking kon niet laden: {(error as Error).message}</Notice>;
  if (isLoading || !data) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Laden">
        <Skeleton className="h-12 w-full" />
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-full" />
        ))}
      </div>
    );
  }

  const urenOff = Math.abs(data.total_hours_diff) > 0.01;
  const days = data.days.filter((d) => d.own_shifts.length > 0 || d.trigion_lines.length > 0);

  return (
    <div className="space-y-5">
      <dl className="grid grid-cols-3 gap-3">
        <Stat label="Jouw diensten" value={formatHours(data.own_total_hours)} />
        <Stat
          label="Urencontrole"
          value={data.has_urencontrole ? formatHours(data.trigion_total_hours) : 'Nog niet binnen'}
          className={!data.has_urencontrole ? 'text-slate-400' : undefined}
        />
        <Stat
          label="Verschil"
          value={data.has_urencontrole ? signed(data.total_hours_diff, formatHours) : '–'}
          className={!data.has_urencontrole ? 'text-slate-400' : urenOff ? 'text-amber-200' : 'text-money'}
        />
      </dl>

      {/* Bedragen (loonstrook, toeslagen) staan in het overzicht van de periode; hier alleen de uren per dag. */}
      {!data.has_urencontrole && (
        <Notice tone="info">De urencontrole van deze periode is er nog niet. Zodra Trigion hem stuurt, zie je hier per dag of de uren kloppen.</Notice>
      )}

      {data.has_urencontrole && (
      <section className="space-y-1">
        <h3>Per dag</h3>
        {days.length === 0 ? (
          <p className="t-muted">Geen diensten of uren in deze periode.</p>
        ) : (
          <div className="list">
            {days.map((d) => {
              const off = Math.abs(d.diff) > 0.01;
              return (
                <div key={d.date} className="grid grid-cols-[4.5rem_minmax(0,1fr)_auto] items-start gap-x-3 py-2.5">
                  <span className={cn('text-sm', off ? 'font-medium text-slate-50' : 'text-slate-300')}>
                    {formatDate(d.date, 'EEE dd-MM')}
                  </span>
                  <div className="min-w-0 space-y-1 text-sm">
                    <Regel label="App">
                      {d.own_shifts.length === 0
                        ? null
                        : d.own_shifts.map((s, i) => (
                            <div key={i} className="truncate">
                              <span className="t-num text-slate-200">
                                {s.start}–{s.end}
                              </span>{' '}
                              <span className="text-slate-400">({formatHours(s.hours)})</span>
                              {s.object && <span className="text-slate-500"> {s.object}</span>}
                            </div>
                          ))}
                    </Regel>
                    <Regel label="Trigion">
                      {d.trigion_lines.length === 0
                        ? null
                        : d.trigion_lines.map((l, i) => (
                            <div key={i} className="truncate">
                              <span className="t-num text-slate-200">
                                {l.start ?? '–'}–{l.end ?? '–'}
                              </span>{' '}
                              <span className="text-slate-400">({formatHours(l.hours ?? 0)})</span>
                              {l.bijz_code && <span className="badge-neutral ml-1.5">{l.bijz_code}</span>}
                              {l.omschrijving && <span className="text-slate-500"> {l.omschrijving}</span>}
                            </div>
                          ))}
                    </Regel>
                  </div>
                  <span className={cn('t-num text-right text-sm font-medium', off ? 'text-amber-200' : 'text-slate-500')}>
                    {signed(d.diff, formatHours)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>
      )}
    </div>
  );
}

function Stat({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className="min-w-0">
      <dt className="t-label">{label}</dt>
      <dd className={cn('t-num mt-1 truncate text-base font-semibold text-slate-100', className)}>{value}</dd>
    </div>
  );
}

function Regel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <span className="t-label w-12 shrink-0 pt-0.5">{label}</span>
      <div className="min-w-0 flex-1">{children ?? <span className="text-slate-500">–</span>}</div>
    </div>
  );
}

function FlagRow({ flag }: { flag: NonNullable<CompareResult['flags']>[number] }) {
  const Icon = flag.level === 'error' ? XCircle : flag.level === 'warn' ? AlertCircle : Info;
  const tone = flag.level === 'error' ? 'text-red-300' : flag.level === 'warn' ? 'text-amber-200' : 'text-slate-400';
  return (
    <li className="flex items-start gap-2 text-sm text-slate-200">
      <Icon className={cn(ICON.sm, 'mt-0.5 shrink-0', tone)} aria-hidden />
      <span>{flag.message}</span>
    </li>
  );
}

function Loonstrook({ data }: { data: CompareResult }) {
  const ls = data.loonstrook!;
  const r = data.reconstructie;
  const brutoOk = r ? Math.abs(r.bruto_diff) < 1 : null;
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <h3>Loonstrook</h3>
        {brutoOk != null && <span className={brutoOk ? 'status-ok' : 'status-warn'}>{brutoOk ? 'Bruto klopt' : 'Bruto wijkt af'}</span>}
      </div>
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Verloonde uren" value={formatHours(ls.verloonde_uren)} />
        <Stat label="Bruto" value={formatMoney(ls.brutoloon)} />
        <Stat label="Reiskosten" value={formatMoney(ls.reiskosten_onbelast)} />
        <Stat label="Uitbetaald" value={formatMoney(ls.uitbetaald)} className="text-money" />
      </dl>
      {r && (
        <p className="t-muted">
          Nagerekend met de CAO: app <span className="t-num text-slate-200">{formatMoney(r.eigen_bruto)}</span>, loonstrook{' '}
          <span className="t-num text-slate-200">{formatMoney(r.loon_bruto)}</span>, verschil{' '}
          <span className={cn('t-num', brutoOk ? 'text-money' : 'text-amber-200')}>{signed(r.bruto_diff, formatMoney)}</span>.
        </p>
      )}
    </section>
  );
}

function OrtTable({ rows }: { rows: NonNullable<CompareResult['ort_compare']> }) {
  const relevant = rows.filter((r) => r.eigen_uren > 0 || r.loon_uren > 0);
  if (relevant.length === 0) return null;
  return (
    <section className="space-y-1">
      <h3>Toeslagen per soort</h3>
      <div className="overflow-x-auto">
        <table className="table min-w-[420px] [&_td:first-child]:pl-0 [&_th:first-child]:pl-0 [&_td:last-child]:pr-0 [&_th:last-child]:pr-0">
          <thead>
            <tr>
              <th>Soort</th>
              <th className="r">App</th>
              <th className="r">Loonstrook</th>
              <th className="r">Verschil</th>
            </tr>
          </thead>
          <tbody>
            {relevant.map((r) => {
              const urenOk = Math.abs(r.uren_diff) < 0.05;
              const bedragOk = Math.abs(r.bedrag_diff) < 0.5;
              return (
                <tr key={r.categorie}>
                  <td className="text-slate-100">{r.categorie}</td>
                  <td className="r text-slate-300">{formatHours(r.eigen_uren)}</td>
                  <td className="r text-slate-300">{formatHours(r.loon_uren)}</td>
                  <td className={cn('r', urenOk && bedragOk ? 'text-slate-500' : 'text-amber-200')}>
                    {signed(r.uren_diff, formatHours)}
                    <div className="text-xs">{signed(r.bedrag_diff, formatMoney)}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
